import React, { useState, useMemo, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { useUserAccountsData } from './hooks/useUserAccountsData';
import UserAccountsTable from './components/UserAccountsTable';
import UserAccountsForm from './components/UserAccountsForm'; 
import { useLanguage } from '../../../../contexts/LanguageContext';
import UserAccountsFilter from './components/UserAccountFilter'; 

const UserAccounts = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const [selectedUser, setSelectedUser] = useState(null);
    const [displayModal, setDisplayModal] = useState(false);
    const [currentFormData, setCurrentFormData] = useState(null);
    const toastRef = useRef(null);

    const {
        users,
        loading,
        filters,
        globalFilterValue,
        onGlobalFilterChange,
        handleSave,
        handleRemove,
        usageTypeOptions,
        totalCount,
        lazyParams,
        onPage,
        onSort,
        handleAdvancedFilterChange,
        handleResetFilters, 
        error,
        refetch,
        hasFormDataChanged,
        setFormOriginalData
    } = useUserAccountsData();

    const lang = Number(getCurrentLanguageId?.()); 
 
    const handleEdit = (rowData) => {
        setSelectedUser(rowData);
        setFormOriginalData(rowData);
        setDisplayModal(true);
    };

    const handleNew = () => {
        setSelectedUser(null);
        setFormOriginalData(null);
        setDisplayModal(true);
    };

    const handleHide = () => {
        setDisplayModal(false);
        setSelectedUser(null);
        setCurrentFormData(null);
        setFormOriginalData(null);
    };

    const onFormSubmit = async (formData) => {
        try {
            const result = await handleSave(formData);
            if (result?.success) {
                toastRef.current?.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: selectedUser ? th('userAccounts.updateSuccess') : th('userAccounts.createSuccess'),
                    life: 3000
                });
                handleHide();
            }
        } catch (error) {
            toastRef.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: error.message || th('userAccounts.saveError'),
                life: 5000
            });
        }
    };

    const onFormRemove = async (user) => {
        try {
            const result = await handleRemove(user);
            if (result?.success) {
                toastRef.current?.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: th('userAccounts.deleteSuccess'),
                    life: 3000
                });
                handleHide();
            }
        } catch (error) {
            toastRef.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: error.message || th('userAccounts.deleteError'),
                life: 5000
            });
        }
    };

    const checkFormDataChanged = () => {
        return hasFormDataChanged(currentFormData);
    };

    const handleFormDataChange = (formData) => {
        setCurrentFormData(formData);
    };

    return (
        <div className="card">
            <Toast ref={toastRef} />

            <div className="flex justify-content-between align-items-center mb-4">
                <div className="flex align-items-center gap-3">
                    <h1 className="m-0">{th('userAccounts.title')}</h1>
                </div>
            </div>

            {error && (
                <Message
                    severity="error"
                    className="mb-3 w-full"
                    content={
                        <div className="flex align-items-center justify-content-between w-full">
                            <div className="flex align-items-center">
                                <i className="pi pi-times-circle mr-2"></i>
                                <span>
                                    {th('userAccounts.loadError')}: {error.message}
                                </span>
                            </div>
                            <button className="p-button p-button-text p-button-sm" onClick={() => refetch()}>
                                <i className="pi pi-refresh mr-1"></i>
                                {th('common.retry')}
                            </button>
                        </div>
                    }
                />
            )}

            <div className="mb-2">
                <UserAccountsFilter initialFilters={{ activeOnly: null, country: '' }} onApplyFilters={handleAdvancedFilterChange} onResetFilters={handleResetFilters} initiallyOpen={false} className="advanced-filter-container" />{' '}
            </div>

            <UserAccountsTable
                users={users}
                loading={loading}
                filters={filters}
                globalFilterValue={globalFilterValue}
                onGlobalFilterChange={onGlobalFilterChange}
                onEdit={handleEdit}
                onNew={handleNew}
                totalCount={totalCount}
                lazyParams={lazyParams}
                onPage={onPage}
                onSort={onSort}
            />

            <UserAccountsForm
                visible={displayModal}
                onHide={handleHide}
                onSubmit={onFormSubmit}
                onRemove={onFormRemove}
                selectedUser={selectedUser}
                loading={loading}
                usageTypeOptions={usageTypeOptions}
                isEditMode={!!selectedUser}
                hasFormDataChanged={checkFormDataChanged}
                onFormDataChange={handleFormDataChange}
            />
        </div>
    );
};

export default UserAccounts;
